<template>
  <div class="variants__wrapper">
    <div class="variants">
      <Button
        v-for="variant in variants"
        :key="variant.id"
        theme="secondary"
        form="outline"
        class="variant"
        :class="{
          'variant--disabled': variantDisabled(variant),
          'variant--selected': selectedVariant?.id === variant.id,
        }"
        @click="handleVariantSelect(variant)"
      >
        {{ variant.title }}
      </Button>
    </div>
    <div class="variants-meta">
      <div class="variants-meta-item variants-meta-item--start">
        <button class="variants-button" @click="setOpenSizeGuide(true)">
          <span class="text">{{ buttonText.sizeGuide }}</span>
        </button>
      </div>
      <span
        v-if="hasVariantsOutOfStock"
        class="variants-meta-separator"
        aria-hidden="true"
        >·</span
      >
      <div
        v-if="hasVariantsOutOfStock"
        class="variants-meta-item variants-meta-item--center"
      >
        <button class="variants-button" @click="handleStockNotificationClick">
          <span class="text">{{ buttonText.outOfStock }}</span>
        </button>
      </div>
      <span
        v-if="hasVariantsOutOfStock && showFitQuizButton"
        class="variants-meta-separator"
        aria-hidden="true"
        >·</span
      >
      <div
        v-if="showFitQuizButton"
        class="variants-meta-item variants-meta-item--end"
      >
        <Easysize
          class="variants-fit-quiz"
          text=""
          underline-text="Try Our Fit Quiz"
          compact
        />
      </div>
    </div>
  </div>
</template>

<script>
// Import components
import Button from "@/components/ButtonV2.vue";
import Easysize from "@/components/product-display/Easysize.vue";

// Import internal modules
import { mapState, mapActions, mapGetters } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

export default {
  name: "Variants",
  components: {
    Button,
    Easysize,
  },
  data() {
    return {
      buttonText: {
        sizeGuide: "Size Guide",
        outOfStock: "Size Not In Stock?",
      },
    };
  },
  computed: {
    ...mapState("product", ["product", "selectedVariant"]),
    ...mapGetters("product", ["variants"]),
    showFitQuizButton() {
      return this.product?.productType !== "Jewellery";
    },
    hasVariantsOutOfStock() {
      return this.variants.some((variant) => {
        return !(variant.quantityAvailable > 0 && variant.availableForSale);
      });
    },
    isSoldOut() {
      if (this.availableForPreOrder(this.selectedVariant)) {
        return false;
      }

      return (
        !this.selectedVariant.availableForSale ||
        this.selectedVariant.quantityAvailable <= 0
      );
    },
  },
  methods: {
    ...mapActions("product", [
      "selectVariantForPurchase",
      "setSelectedVariant",
      "setOpenSizeGuide",
    ]),
    ...mapActions("add-to-bag", [
      "showCompactStockNotif",
      "openStockNotification",
      "openWaitlist",
    ]),
    handleVariantSelect(variant) {
      this.selectVariantForPurchase(variant);

      const { waitlistVersion = 0 } = getFeatureToggles();

      // Waitlist A/B test arms replace the inline compact stock form:
      // version 1 leaves the CTA to read "JOIN THE WAITLIST" (opened on
      // click), version 2 opens the waitlist drawer immediately.
      if (waitlistVersion !== 0) {
        if (waitlistVersion === 2 && this.isSoldOut) {
          this.openWaitlist();
        }
        return;
      }

      this.showCompactStockNotif({
        product: this.product,
        variant,
        show: this.isSoldOut,
      });
    },
    handleStockNotificationClick() {
      this.setSelectedVariant(null);
      this.openStockNotification({
        product: this.product,
      });
    },
    variantDisabled(variant) {
      const variantIsPreOrder = this.availableForPreOrder(variant);

      if (variantIsPreOrder) {
        return false;
      }

      return !variant.availableForSale || variant.quantityAvailable <= 0;
    },
    preOrder(variant) {
      const preorder = variant?.randbPreorder?.value;
      if (!preorder) return false;

      return JSON.parse(preorder);
    },
    availableForPreOrder(variant) {
      if (this.product?.tags?.includes("hide-preorder")) return false;
      if (!this.preOrder) return false;

      const {
        enabled = false,
        limit = 0,
        reserved = 0,
      } = this.preOrder(variant);
      const inventory = variant.quantityAvailable || 0;

      return enabled && inventory <= 0 && reserved < limit;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.variants__wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.variants {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;

  @include respond-above(lg) {
    gap: 13px;
  }
}

.variant {
  padding: 0;
  cursor: pointer;
  border-radius: 4px;
  border-color: $color-grey;
  height: 40px;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @include respond-above(lg) {
    height: 40px;
  }

  :deep(.text) {
    @include utility-label(13px);
  }

  &:hover {
    opacity: 1;
  }

  &--disabled {
    background-color: $color-grey-light;
    border-color: $color-grey;
    color: $color-grey;
    text-decoration: line-through;

    :deep(.text) {
      text-decoration: line-through;
    }

    &:hover {
      background-color: $color-grey-light;
      border-color: $color-grey;
      color: $color-grey;
    }
  }

  // Chain the parent class (`.variant.variant--selected`) to raise specificity
  // so the selected style wins the source-order tie against the shared
  // `.button--outline` base style that lives in common.css.
  &.variant--selected {
    background-color: $color-black;
    color: $color-white;
    border-color: $color-black;

    &:hover {
      background-color: $color-black;
      color: $color-white;
    }
  }

  &--selected.variant--disabled {
    border-color: $color-black;
    background-color: $color-grey-light;
    color: $color-black;

    &:hover {
      border-color: $color-black;
      background-color: $color-grey-light;
      color: $color-black;
    }
  }

  &.variant--added-to-bag {
    background-color: $color-black;
    color: $color-white;

    &:hover {
      background-color: $color-black;
      color: $color-white;
    }
  }
}

.variants-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 11px;
  width: 100%;
}

.variants-meta-item {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.variants-meta-separator {
  color: $color-grey-dark;
  @include utility-label(15px);
  line-height: 1;
}

.variants-meta-item--start {
  justify-content: flex-start;
}

.variants-meta-item--center {
  justify-content: center;
}

.variants-meta-item--end {
  justify-content: flex-end;
}

.variants-button {
  padding: 15px 28px;
  background-color: $color-grey-light;
  color: $color-grey-dark;
  border-radius: 4px;
  @include utility-button(13px);
  border: none;
  outline: none;
  text-decoration: none;
  text-align: center;
  text-transform: none;

  padding: 0;
  color: $color-grey-dark;
  background-color: transparent;
  outline: none;
  border: none;
  @include utility-label(15px);

  .text {
    position: relative;
    white-space: nowrap;

    &::after {
      @include editorial-dotted-underline($color-grey);
    }
  }
}

.variants-fit-quiz :deep(.easysize_placeholder) {
  display: none;
}

.variants-fit-quiz :deep(.button) {
  text-transform: none;
  @include utility-label(15px);
  color: $color-grey-dark;

  &.button--underline .text::after {
    @include editorial-dotted-underline($color-grey);
  }
}
</style>
